import { useParams } from 'react-router';
import { MissingResourceError } from '#api/errors.ts';
import { useSuspenseHostQuery } from '#api/hosts.queries.ts';
import { useConditionalRoleQuery } from '#api/roles.queries.ts';
import { Link } from '#components/atoms/link/Link.tsx';
import { CopyPill } from '#components/atoms/pill/copy-pill/CopyPill.tsx';
import { MainLayout } from '#components/patterns/layouts/main/MainLayout.tsx';
import { PageCloser } from '#components/patterns/page-closer/PageCloser.tsx';
import { PageHeader } from '#components/patterns/page-header/PageHeader.tsx';
import { useReturnNavigation } from '#hooks/useAppLocation.ts';
import { usePersistedNetworkFilter } from '#hooks/usePersistedNetworkFilter.tsx';
import { useTagOverrideMap } from '#screens/hosts/form/hooks/useTagOverrideMap.ts';
import { hostsListTo } from '#screens/hosts/listLocation.ts';
import { hostKind } from '#util/host.ts';
import { fromArray, Titled } from '#util/title.ts';
import { HostForm } from '../form/HostForm.tsx';
import { mapHostToForm } from '../form/mapHostToForm.ts';
import { makeOverrideDefaults } from './makeOverrideDefaults.ts';

export function EditHostPage() {
  const { hostId } = useParams() as { hostId: string };
  const returnTo = useReturnNavigation(`/hosts/${hostId}`);
  const {
    data: { data: host },
    error,
    isFetching,
  } = useSuspenseHostQuery(hostId);
  const overrideMap = useTagOverrideMap(host.tags);
  const configOverridesDefaultValues = makeOverrideDefaults(overrideMap);
  const persistedNetworkFilter = usePersistedNetworkFilter();
  const { data: { data: role } = {} } = useConditionalRoleQuery(host.roleID ?? '', {
    enabled: Boolean(host.roleID),
  });
  const roleName = role?.name ?? '';

  // Check if this has been deleted (e.g. in a different tab)
  if (error && error instanceof MissingResourceError && !isFetching) {
    throw error;
  }

  // Wait until we have a role if it exists
  if (host.roleID && !role) return null;

  const defaultValues = { ...mapHostToForm(host, roleName), configOverrides: configOverridesDefaultValues };

  return (
    <Titled title={(title) => fromArray(['Edit', host.name, title])}>
      <MainLayout
        header={
          <PageHeader
            title={
              <>
                <Link to={hostsListTo(hostKind(host), persistedNetworkFilter)} className="a">
                  Hosts
                </Link>
                {' / '}
                <Link to={`/hosts/${host.id}`} className="a">
                  {host.name}
                </Link>{' '}
                / Edit
              </>
            }
            subtitle={
              <span className="mb-8">
                ID: <CopyPill value={host.id} />
              </span>
            }
          >
            <PageCloser fallbackTo={hostsListTo(hostKind(host), persistedNetworkFilter)} />
          </PageHeader>
        }
      >
        <HostForm
          onSuccess={() => returnTo()}
          hostId={host.id}
          kind={hostKind(host)}
          defaultValues={defaultValues}
          staticAddresses={host.staticAddresses}
          configOverrides={host.configOverrides}
        />
      </MainLayout>
    </Titled>
  );
}
